'use client';

import type { PropsWithChildren } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CONFIG } from '@frameio/config';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { AppSheetContext } from '@frameio/shared/contexts';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { TOOLTIP_IDS, tooltipStore } from '@frameio/shared/onboarding';
import {
  Menu,
  MenuGroup,
  Tooltip,
  useActiveWindowBreakpoint,
  useHasMounted,
} from '@frameio/vapor';
import { AccountSwitcherList } from './AccountSwitcherList';
import { ACCOUNT_SWITCHER_MENU_WIDTH } from './constants';
import TEXT from './generated/TEXT/AccountSwitcherMenu';
import { useGetAllAccountsForPrimaryNavPanelQuery } from './gql/queries/generated/GetAllAccountsForPrimaryNavPanel';
import { AccountSettingsNavItem } from './MenuItems/AccountSettingsNavItem';
import { AppThemeSelectionMenuItem } from './MenuItems/AppThemeSelectionMenuItem';
import { BuildIdentifierNavItem } from './MenuItems/BuildIdentifierNavItem';
import { DevToolsMenuItem } from './MenuItems/DevToolsMenuItem';
import { HelpAndSupportMenuItem } from './MenuItems/HelpAndSupportMenuItem';
import { KeyboardShortcutNavItem } from './MenuItems/KeyboardShortcutNavItem';
import { LanguageSelectionMenuItem } from './MenuItems/LanguageSelectionMenuItem';
import { LogoutNavItem } from './MenuItems/LogoutNavItem';
import { MarketingWebsiteMenuItem } from './MenuItems/MarketingWebsiteMenuItem';
import { PremiereIntegrationNavItem } from './MenuItems/PremiereIntegrationNavItem';
import { UserAvatar } from './MenuItems/UserAvatar';
import { WhatsNewMenuItem } from './MenuItems/WhatsNewMenuItem';
import type { onAccountChangedFn } from './types';

TEXT.DEFINE({
  /** ARIA label for the account & settings dropdown menu opened from the avatar button */
  ariaLabelForAccountMenu: 'Account and Settings Menu',
  /** Tooltip label for the avatar button that opens the account & settings menu */
  accountSwitcherTooltip: 'Account & Settings',
});

export const AccountSwitcherMenu = (
  props: PropsWithChildren<{
    activeAccountId: string;
    minimal?: boolean;
    onAccountChanged: onAccountChangedFn;
  }>
) => {
  const breakpoint = useActiveWindowBreakpoint();
  const isMobile = breakpoint === 'base';
  const { setId: setSheetId } = useContext(AppSheetContext);
  const [isShowing, setIsShowing] = useState(false);
  const hasMounted = useHasMounted();
  const { refetch } = useGetAllAccountsForPrimaryNavPanelQuery({
    skip: !hasMounted,
  });
  const isLinkToMarketingSiteEnabled = useFeatureFlagEnabled(
    'v4.link_to_marketing_site'
  );
  const isGlobalNavEnabled = useFeatureFlagEnabled('v4.global_navigation');
  const isWebClient = useIsMatchingClientName('web');

  const tooltip = (
    <Tooltip
      placement={isWebClient ? 'right' : 'bottom'}
      title={TEXT.accountSwitcherTooltip}
    />
  );

  const handleMenuRequestChange = useCallback(
    (nextIsShowing: boolean) => {
      setIsShowing(nextIsShowing);
      if (nextIsShowing) {
        setSheetId?.(null);
        tooltipStore.dismissTooltip(TOOLTIP_IDS.ACCOUNT_MULTI_LANGUAGE);
        refetch();
      }
    },
    [refetch, setSheetId]
  );

  const showDevToolsSheet = CONFIG.showDevToolsSheet;

  const menuContent = useMemo(
    () => (
      <>
        {!props.minimal && (
          <MenuGroup>
            <UserAvatar />
          </MenuGroup>
        )}
        <AccountSwitcherList
          activeAccountId={props.activeAccountId}
          onAccountChanged={props.onAccountChanged}
          setIsShowing={setIsShowing}
        />
        {!props.minimal && isWebClient && (
          <MenuGroup>
            <AccountSettingsNavItem />
            <LanguageSelectionMenuItem />
            {!isMobile && (
              <KeyboardShortcutNavItem trigger="AvatarMenuProject" />
            )}
            <AppThemeSelectionMenuItem />
            {isLinkToMarketingSiteEnabled && <MarketingWebsiteMenuItem />}
          </MenuGroup>
        )}
        {!props.minimal && isGlobalNavEnabled && isWebClient && (
          <MenuGroup>
            {showDevToolsSheet && <DevToolsMenuItem />}
            <WhatsNewMenuItem />
            <HelpAndSupportMenuItem />
          </MenuGroup>
        )}
        <MenuGroup>
          {!isWebClient && <PremiereIntegrationNavItem />}
          <LogoutNavItem />
        </MenuGroup>
        {!isWebClient && (
          <MenuGroup>
            <BuildIdentifierNavItem />
          </MenuGroup>
        )}
      </>
    ),
    [
      showDevToolsSheet,
      props.activeAccountId,
      props.onAccountChanged,
      props.minimal,
      isWebClient,
      isMobile,
      isGlobalNavEnabled,
      isLinkToMarketingSiteEnabled,
    ]
  );

  const placement = useMemo(() => {
    if (isGlobalNavEnabled) {
      return 'bottom';
    }
    return isWebClient ? 'right-end' : 'bottom';
  }, [isGlobalNavEnabled, isWebClient]);

  return (
    <Menu
      content={menuContent}
      data-testid="avatar-button"
      isShowing={isShowing}
      onRequestChange={handleMenuRequestChange}
      placement={placement}
      tooltip={tooltip}
      width={
        isGlobalNavEnabled ? `${ACCOUNT_SWITCHER_MENU_WIDTH}px` : undefined
      }
      /** making sure that the menu appears below the AvatarEditorDialog which has a z-index of 1502 */
      zIndex={1501}
    >
      {props.children}
    </Menu>
  );
};
